import { useSuspenseQuery } from '@tanstack/react-query';
import type { TypeGetWorkApplicationsDetailResponse } from '~/api/workApplications';
import { registerMockAPI } from '~/mocks/mockApis';

import { axiosInstance } from '~/services/fetchServiceV2';

/** @description 지원내역 상세 조회 */
export const useGetWorkApplyDetail = (workApplicationId: string) => {
  const { data } = useSuspenseQuery({
    queryKey: ['work-apply-detail', workApplicationId],
    queryFn: async () => {
      const endpoint = `/work-applications/${workApplicationId}`;
      return await axiosInstance.get<TypeGetWorkApplicationsDetailResponse>(endpoint);
    },
    gcTime: 0,
    retry: 1,
    select: (response) => response.data.data,
  });

  return { data };
};

registerMockAPI<TypeGetWorkApplicationsDetailResponse>({
  method: 'get',
  endpoint: '/work-applications/:workApplicationId',
  label: '지원내역/상세 조회',
  scenarios: [
    {
      name: '자차 (주차 정보 포함)',
      response: {
        code: '0000',
        message: '성공',
        data: {
          tid: 'tid-001',
          workDate: '2025-06-25',
          applicationDate: '2025-06-20',
          task: { code: 'MID_SORT', text: '중간분류', description: '중간분류 작업' },
          commutingMethod: { code: 'CAR', text: '자차' },
          workLocationName: '김포',
          lotAddress: '경기도 김포시 고촌읍 향산리 127',
          roadAddress: '경기도 김포시 고촌읍 아라육로75번길 100',
          latitude: 37.589858,
          longitude: 126.7921041,
          locationUrl: 'https://map.naver.com/p/entry/place/38715440',
          parkingGuide: '건물 내 8층 옥상 주차장 이용',
          applications: [
            {
              workApplicationId: '1',
              priority: { code: 'FIRST', text: '1지망' },
              workShift: '13:00 ~ 22:00',
              workEnvironment: { code: 'COLD', text: '냉장' },
              breakTimeMinutes: 80,
              dailyWage: 88200,
              mealProvisionType: { code: 'DINNER', text: '석식 제공' },
            },
            {
              workApplicationId: '2',
              priority: { code: 'SECOND', text: '2지망' },
              workShift: '09:00 ~ 18:00',
              workEnvironment: { code: 'NORMAL', text: '상온' },
              breakTimeMinutes: 60,
              dailyWage: 82800,
              mealProvisionType: { code: 'LUNCH', text: '중식 제공' },
            },
          ],
        },
      } satisfies TypeGetWorkApplicationsDetailResponse,
    },
    {
      name: '셔틀 (주차 정보 없음)',
      response: {
        code: '0000',
        message: '성공',
        data: {
          tid: 'tid-002',
          workDate: '2025-06-22',
          applicationDate: '2025-06-18',
          task: { code: 'OUTBOUND', text: '출고', description: '출고 작업' },
          commutingMethod: { code: 'SHUTTLE', text: '셔틀' },
          workLocationName: '평택',
          lotAddress: '경기도 평택시 포승읍 만호리 271-1',
          roadAddress: '경기도 평택시 포승읍 평택항로 166',
          latitude: 36.9685,
          longitude: 126.9046,
          locationUrl: 'https://map.naver.com/p/entry/place/12345678',
          parkingGuide: null,
          applications: [
            {
              workApplicationId: '3',
              priority: { code: 'FIRST', text: '1지망' },
              workShift: '16:00 ~ 00:50',
              workEnvironment: { code: 'HUB', text: 'HUB' },
              breakTimeMinutes: 50,
              dailyWage: 91000,
              mealProvisionType: { code: 'LUNCH', text: '중식 제공' },
            },
            {
              workApplicationId: '4',
              priority: { code: 'SECOND', text: '2지망' },
              workShift: '13:00 ~ 22:00',
              workEnvironment: { code: 'COLD', text: '냉장' },
              breakTimeMinutes: 80,
              dailyWage: 88200,
              mealProvisionType: { code: 'DINNER', text: '석식 제공' },
            },
          ],
        },
      } satisfies TypeGetWorkApplicationsDetailResponse,
    },
    {
      name: '1지망만 (대중교통)',
      response: {
        code: '0000',
        message: '성공',
        data: {
          tid: 'tid-003',
          workDate: '2025-06-18',
          applicationDate: '2025-06-15',
          task: { code: 'INBOUND', text: '입고', description: '입고 작업' },
          commutingMethod: { code: 'SELF', text: '대중교통 및 기타' },
          workLocationName: '안산',
          lotAddress: '경기도 안산시 단원구 원시동 786-1',
          roadAddress: '경기도 안산시 단원구 산단로163번길 40',
          latitude: 37.3235,
          longitude: 126.8507,
          locationUrl: null,
          parkingGuide: null,
          applications: [
            {
              workApplicationId: '5',
              priority: { code: 'FIRST', text: '1지망' },
              workShift: '08:00 ~ 17:00',
              workEnvironment: { code: 'ALL_TEMP', text: '전체온도' },
              breakTimeMinutes: 60,
              dailyWage: 84500,
              mealProvisionType: { code: 'NONE', text: '미제공' },
            },
          ],
        },
      } satisfies TypeGetWorkApplicationsDetailResponse,
    },
    {
      name: '1지망만 (자차)',
      response: {
        code: '0000',
        message: '성공',
        data: {
          tid: 'tid-004',
          workDate: '2025-06-13',
          applicationDate: '2025-06-10',
          task: { code: 'INVENTORY', text: '재고관리', description: '재고관리 작업' },
          commutingMethod: { code: 'CAR', text: '자차' },
          workLocationName: '군포',
          lotAddress: '경기도 군포시 산본동 1153-8',
          roadAddress: '경기도 군포시 산본로323번길 20',
          latitude: 37.3609,
          longitude: 126.9339,
          locationUrl: null,
          parkingGuide: 'B동 지하 1층 주차장 이용',
          applications: [
            {
              workApplicationId: '6',
              priority: { code: 'FIRST', text: '1지망' },
              workShift: '09:00 ~ 18:00',
              workEnvironment: { code: 'NORMAL', text: '상온' },
              breakTimeMinutes: 60,
              dailyWage: 82800,
              mealProvisionType: { code: 'LUNCH', text: '중식 제공' },
            },
          ],
        },
      } satisfies TypeGetWorkApplicationsDetailResponse,
    },
  ],
});
